
import React, { useState, useCallback } from 'react';
import Cropper, { Area, Point } from 'react-easy-crop';

interface ImageCropperProps {
  image: string;
  aspect?: number;
  cropShape?: 'rect' | 'round';
  onComplete: (croppedImage: string) => void;
  onCancel: () => void;
}

const ImageCropper: React.FC<ImageCropperProps> = ({ 
  image, 
  aspect = 1, 
  cropShape = 'rect', 
  onComplete, 
  onCancel 
}) => {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const onCropComplete = useCallback((_area: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.addEventListener('load', () => resolve(img));
      img.addEventListener('error', (error) => reject(error));
      img.setAttribute('crossOrigin', 'anonymous');
      img.src = url;
    });

  const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: Area,
    rotation: number
  ): Promise<string> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) return '';

    const rotRad = (rotation * Math.PI) / 180;
    const { width: bWidth, height: bHeight } = {
      width: Math.abs(Math.cos(rotRad) * image.width) + Math.abs(Math.sin(rotRad) * image.height),
      height: Math.abs(Math.sin(rotRad) * image.width) + Math.abs(Math.cos(rotRad) * image.height),
    };

    canvas.width = bWidth;
    canvas.height = bHeight;

    ctx.translate(bWidth / 2, bHeight / 2);
    ctx.rotate(rotRad);
    ctx.translate(-image.width / 2, -image.height / 2);
    ctx.drawImage(image, 0, 0);

    const data = ctx.getImageData(
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height
    );

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    ctx.putImageData(data, 0, 0);

    return canvas.toDataURL('image/jpeg', 0.82);
  };

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    setIsProcessing(true);
    try {
      const result = await getCroppedImg(image, croppedAreaPixels, rotation);
      onComplete(result);
    } catch (e) {
      console.error(e);
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[600] flex flex-col bg-brand-navy/98 backdrop-blur-3xl overflow-hidden">
      {isProcessing && (
        <div className="absolute inset-0 z-[700] bg-brand-navy/60 backdrop-blur-md flex flex-col items-center justify-center animate-in fade-in duration-300">
           <div className="w-16 h-16 border-4 border-brand-orange border-t-transparent rounded-full animate-spin mb-4"></div>
           <p className="text-white font-black uppercase text-[10px] tracking-[0.3em] italic">Otimizando sua imagem...</p>
        </div>
      )}

      <div className="flex-1 relative">
        <Cropper
          image={image}
          crop={crop}
          zoom={zoom}
          rotation={rotation}
          aspect={aspect}
          cropShape={cropShape}
          showGrid={false}
          onCropChange={setCrop}
          onRotationChange={setRotation}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
        />
      </div>
      
      <div className="p-8 sm:p-12 bg-white rounded-t-[3.5rem] shadow-[0_-20px_50px_rgba(0,0,0,0.3)] animate-in slide-in-from-bottom-10 duration-500">
        <div className="max-w-md mx-auto space-y-8">
          <div className="text-center">
            <h3 className="text-xl font-title font-black text-brand-navy uppercase italic tracking-tighter">Enquadramento Profissional</h3>
            <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest mt-1">Sua foto será exibida exatamente como no recorte abaixo</p>
          </div>

          <div className="space-y-6">
             <div className="flex items-center gap-6">
               <span className="text-[10px] font-black text-gray-300 uppercase shrink-0">Zoom</span>
               <input
                 type="range"
                 value={zoom}
                 min={1}
                 max={3}
                 step={0.1}
                 onChange={(e) => setZoom(Number(e.target.value))}
                 className="flex-1 h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-brand-orange"
               />
             </div>

             <div className="flex justify-center gap-4">
               <button 
                 onClick={() => setRotation(r => r - 90)}
                 className="bg-gray-50 hover:bg-gray-100 text-brand-navy p-4 rounded-2xl transition-all"
                 title="Girar para Esquerda"
               >
                 <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10 19l-7-7m0 0l7-7m-7 7h18"/></svg>
               </button>
               <button 
                 onClick={() => setRotation(r => r + 90)}
                 className="bg-gray-50 hover:bg-gray-100 text-brand-navy p-4 rounded-2xl transition-all"
                 title="Girar para Direita"
               >
                 <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3"/></svg>
               </button>
             </div>
          </div>

          <div className="flex gap-4">
            <button 
              disabled={isProcessing}
              onClick={onCancel}
              className="flex-1 bg-gray-50 text-gray-400 font-black py-6 rounded-2xl uppercase tracking-widest text-[10px] hover:bg-gray-100 transition-colors"
            >
              Descartar
            </button>
            <button 
              disabled={isProcessing}
              onClick={handleConfirm}
              className="flex-[2] bg-brand-orange text-white font-black py-6 rounded-2xl shadow-xl shadow-brand-orange/20 uppercase tracking-widest text-[10px] hover:scale-[1.02] active:scale-95 transition-all"
            >
              Aplicar Ajuste
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper;
